import { useState } from 'react';
import { Eye, ListPlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** contact_group_members.additional_details shown in contact lists. */

type Details = Record<string, unknown> | null | undefined;

export function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.map((v) => formatDetailValue(v)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function entries(details: Details): [string, string][] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return [];
  return Object.entries(details).map(([k, v]) => [k, formatDetailValue(v)]);
}

/**
 * The backend's custom fields text ("Key: value | Key2: value2", one line per contact) as
 * key → value of the first contact that has any.
 */
export function detailsFromText(text: unknown): Record<string, string> {
  const line = String(text || '').split('\n').find((l) => l.trim()) || '';
  const out: Record<string, string> = {};
  for (const part of line.split(' | ')) {
    const i = part.indexOf(': ');
    if (i > 0) out[part.slice(0, i)] = part.slice(i + 2).trim();
  }
  return out;
}

/** "Key: value; Key2: value2" for CSV export. */
export function additionalDetailsText(details: Details): string {
  const list = entries(details);
  return list.length ? list.map(([k, v]) => `${k}: ${v}`).join('; ') : '-';
}

/** Compact "View (N)" button; the details open in a modal. */
export function AdditionalDetailsCell({ details, contactName }: { details: Details; contactName?: string }) {
  const [open, setOpen] = useState(false);
  const list = entries(details);
  if (!list.length) return <span className="text-muted-foreground">—</span>;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
      >
        <Eye className="h-3.5 w-3.5" />
        View ({list.length})
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListPlus className="h-4 w-4 text-primary" />
              Customer Additional Details
            </DialogTitle>
            <DialogDescription>
              {contactName ? `${contactName} · ` : ''}
              {list.length} detail{list.length === 1 ? '' : 's'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">Field</th>
                  <th className="px-3 py-2 font-semibold">Value</th>
                </tr>
              </thead>
              <tbody>
                {list.map(([k, v]) => (
                  <tr key={k} className="border-t border-border/50 align-top">
                    <td className="w-2/5 px-3 py-2 font-medium text-foreground">{k}</td>
                    <td className="whitespace-pre-wrap break-words px-3 py-2 text-muted-foreground">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
